// Shared by build-time HTML and Vue so crawler data follows the visible copy.
const faqGroups = [
  { id: "services", topics: ["homeCare"] },
  { id: "account", topics: ["joining"] },
  { id: "payments", topics: ["paymentMethod", "paymentTiming", "paymentAssurance", "paymentTransparency", "platformFee"] },
  { id: "shifts", topics: ["shiftFrequency", "shiftCancellation"] },
  { id: "insurance", topics: ["socialInsurance"] }
]
const organisationFaqGroups = [
  { id: "billing", topics: ["hiddenCosts", "accounting", "rates", "commitment"] },
  { id: "coverage", topics: ["nurseSelection", "workingHours", "reviews", "coverageSpeed"] },
  { id: "relationship", topics: ["payroll", "exclusivity"] }
]
const organisationFaqTopics = organisationFaqGroups.flatMap(group => group.topics)
const faqTopics = faqGroups.flatMap(group => group.topics)
const organisationBenefits = ["postShifts", "flexibility", "qualified", "coverage", "support"]
const organisationNeeds = ["needsRole", "needsSchedule", "needsSetting"]
const siteName = "All Care Cyprus"
const homeTitle = "All Care Cyprus | Nursing Shifts and Healthcare Staffing"
const homeDescription = "All Care Cyprus connects nurses and healthcare professionals with healthcare organisations across Cyprus. Explore flexible shifts, staffing support, and contact details for the All Care team."
const ratesTitle = "Healthcare Shift Rates | All Care Cyprus"
const ratesDescription = "Explore hourly healthcare shift rates by role for day, night and holiday shifts in Cyprus."
const organisationTitle = "Healthcare Staffing for Organisations | All Care Cyprus"
const organisationDescription = "Discuss your nursing staffing needs with All Care Cyprus and manage staffing requests through your organisation’s portal."

function siteUrl(base = "https://allcare.com.cy") {
  return base.replace(/\/+$/, "")
}

function homeGraph(t, base, language = "en") {
  const url = siteUrl(base)
  const organization = { "@id": url + "/#organization" }
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization", ...organization,
        name: siteName, url: url + "/", logo: url + "/logo.png",
        description: homeDescription, email: "info@allcare.com.cy",
        telephone: "+35794099400",
        areaServed: { "@type": "Country", name: "Cyprus" },
        sameAs: [
          "https://www.facebook.com/profile.php?id=61552681300035",
          "https://www.instagram.com/allcare.cyprus/"
        ]
      },
      {
        "@type": "WebSite", "@id": url + "/#website",
        url: url + "/", name: siteName, publisher: organization
      },
      {
        "@type": "WebPage", "@id": url + "/#webpage",
        url: url + "/", name: homeTitle, description: homeDescription,
        inLanguage: language, isPartOf: { "@id": url + "/#website" },
        about: organization,
        hasPart: { "@id": url + "/#faq" }
      },
      {
        "@type": "Service", "@id": url + "/#staffing",
        name: t("premium.forOrganisations"),
        serviceType: "Healthcare staffing",
        description: t("premium.organisationDescription"),
        url: url + "/organisations", provider: organization,
        areaServed: { "@type": "Country", name: "Cyprus" }
      },
      {
        "@type": "FAQPage", "@id": url + "/#faq",
        url: url + "/#faq", inLanguage: language,
        isPartOf: { "@id": url + "/#webpage" },
        mainEntity: faqTopics.map(topic => ({
          "@type": "Question", name: t("faq." + topic + ".question"),
          acceptedAnswer: { "@type": "Answer", text: t("faq." + topic + ".answer") }
        }))
      }
    ]
  }
}

function publicHead(page, base) {
  const home = page === "home"
  const rates = page === "rates"
  const title = home ? homeTitle : rates ? ratesTitle : organisationTitle
  const description = home ? homeDescription : rates ? ratesDescription : organisationDescription
  const url = siteUrl(base) + (home ? "/" : rates ? "/shift-rates" : "/organisations")
  const image = siteUrl(base) + (home || rates ? "/logo.png" : "/images/organisation-partnership-generated.jpg")
  return {
    title,
    meta: [
      { name: "description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: siteName },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: url },
      { property: "og:image", content: image },
      { property: "og:image:alt", content: home || rates ? siteName + " logo" : "Nursing professionals discussing staffing with a healthcare manager" },
      { name: "twitter:card", content: home || rates ? "summary" : "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: image }
    ],
    link: [{ rel: "canonical", href: url }]
  }
}

function organisationGraph(t, base, language = "en") {
  const url = siteUrl(base)
  const page = url + "/organisations"
  const shared = homeGraph(t, base, language)["@graph"]
    .filter(node => ["Organization", "WebSite", "Service"].includes(node["@type"]))
  return {
    "@context": "https://schema.org",
    "@graph": [
      ...shared,
      {
        "@type": "WebPage", "@id": page + "#webpage",
        url: page, name: organisationTitle, description: t("experience.orgIntro"),
        inLanguage: language, isPartOf: { "@id": url + "/#website" },
        mainEntity: { "@id": url + "/#staffing" },
        breadcrumb: { "@id": page + "#breadcrumb" },
        hasPart: { "@id": page + "#faq" },
        primaryImageOfPage: {
          "@type": "ImageObject", url: url + "/images/organisation-partnership-generated.jpg",
          width: 1536, height: 1024, caption: t("organisationPage.heroAlt")
        }
      },
      {
        "@type": "FAQPage", "@id": page + "#faq",
        url: page + "#faq", inLanguage: language,
        isPartOf: { "@id": page + "#webpage" },
        mainEntity: organisationFaqTopics.map(topic => ({
          "@type": "Question", name: t("organisationFaq." + topic + ".question"),
          acceptedAnswer: { "@type": "Answer", text: t("organisationFaq." + topic + ".answer") }
        }))
      },
      {
        "@type": "BreadcrumbList", "@id": page + "#breadcrumb",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: siteName, item: url + "/" },
          { "@type": "ListItem", position: 2, name: t("experience.organisations"), item: page }
        ]
      }
    ]
  }
}

function ratesGraph(base, language = "en") {
  const url = siteUrl(base) + "/shift-rates"
  return {"@context": "https://schema.org", "@type": "WebPage", "@id": url + "#webpage", url, name: ratesTitle, description: ratesDescription, inLanguage: language}
}

function serializeGraph(graph) {
  return JSON.stringify(graph).replace(/</g, "\\u003c")
}

module.exports = { ratesGraph, faqGroups, faqTopics, organisationFaqGroups, organisationFaqTopics, organisationBenefits, organisationNeeds, homeGraph, organisationGraph, publicHead, serializeGraph, siteUrl }
